import { bffJson, type ReadLane } from "@/shared/api/client";
import { relationshipEnded, relationshipStarted } from "@/domain/clients/client-relationship";
import { clientLinkQuery, linkOwnerCorporationId } from "@/domain/clients/individual-client";
import { fetchIndividualById } from "@/domain/clients/individuals";
import { readQuantPortfolio, readTransactionSet } from "@/domain/quant/suggested-portfolio-client";
import { orderSetOf, setQuantifiedAt } from "@/features/order-instructions/lib/order-set";
import type { ClientKind, ClientLink } from "@/features/order-instructions/lib/order-stages";

export interface SuggestedPortfolioWire {
  id: string;
  status?: string | null;
  quant_source_portfolio_id?: string | null;
  quant_portfolio_transaction_set_id?: string | null;
  presentation_binary_file_id?: string | null;
  order_instruction_binary_file_id?: string | null;
  awaiting_approval_at?: string | null;
  approved_at?: string | null;
  in_execution_at?: string | null;
  executed_at?: string | null;
}

export async function listClientLinks(
  financialAdvisorId: string | null,
  lane: ReadLane = {},
): Promise<ClientLink[]> {
  const query = clientLinkQuery({
    ownerCorporationId: await linkOwnerCorporationId(),
    financialAdvisorId: financialAdvisorId ?? undefined,
  });
  const [individuals, corporations] = await Promise.all([
    bffJson<unknown>(`v1/individual-client?${query}`, lane),
    bffJson<unknown>(`v1/corporation-client?${query}`, lane),
  ]);
  return [
    ...activeLinks(individuals, "individual", "individual_id"),
    ...activeLinks(corporations, "corporation", "corporation_id"),
  ];
}

export interface InstructionSummary {
  id: string;
  createdAt: string | null;
  advisorId: string | null;
  kind: ClientKind;
  linkId: string;
  adviceIndividualId: string | null;
  adviceCorporationId: string | null;
  status: InstructionStatus;
  documentFileId: string | null;
  sealedFileId: string | null;
  signers: { signed: number; total: number } | null;
}

export type InstructionStatus = "DRAFT" | "PENDING" | "COMPLETED" | "CANCELED";

export async function readInstructionSummaries(
  financialAdvisorId: string,
  lane: ReadLane = {},
): Promise<InstructionSummary[]> {
  const rows = await bffJson<unknown>(
    `v1/order-instruction/summary?financial_advisor_id=${encodeURIComponent(financialAdvisorId)}&status=${LISTED_STATUSES}`,
    lane,
  );
  return arrayOf<InstructionSummaryWire>(rows).flatMap((row): InstructionSummary[] => {
    const individual = nonEmpty(row.individual_client_id);
    const corporation = nonEmpty(row.corporation_client_id);
    const linkId = individual ?? corporation;
    const status = row.scrive_document ? row.scrive_document.status : "DRAFT";
    if (typeof row.id !== "string" || linkId === null) return [];
    if (!INSTRUCTION_STATUSES.includes(status as InstructionStatus)) return [];
    const recipients = row.scrive_document?.recipients;
    return [
      {
        id: row.id,
        createdAt: nonEmpty(row.created_at),
        advisorId: nonEmpty(row.financial_advisor_id),
        kind: individual ? "individual" : "corporation",
        linkId,
        adviceIndividualId: nonEmpty(row.advice_individual_v1_id),
        adviceCorporationId: nonEmpty(row.advice_corporation_v1_id),
        status: status as InstructionStatus,
        documentFileId: nonEmpty(row.scrive_document?.document_binary_file_id),
        sealedFileId: nonEmpty(row.scrive_document?.document_sealed_binary_file_id),
        signers: Array.isArray(recipients)
          ? {
              signed: recipients.filter((recipient) => recipient?.status === "SIGNED").length,
              total: recipients.length,
            }
          : null,
      },
    ];
  });
}

export function readInstructionSuggestionId(
  instruction: Pick<InstructionSummary, "adviceIndividualId" | "adviceCorporationId">,
  lane: ReadLane = {},
): Promise<string | null> {
  return adviceSuggestionOf(
    {
      advice_individual_v1_id: instruction.adviceIndividualId,
      advice_corporation_v1_id: instruction.adviceCorporationId,
    },
    lane,
  );
}

export async function readSuggestionById(
  suggestionId: string,
  lane: ReadLane = {},
): Promise<SuggestedPortfolioWire | null> {
  const suggestion = await bffJson<unknown>(
    `v1/quant-portfolio/suggested-portfolio/${encodeURIComponent(suggestionId)}`,
    lane,
  );
  return typeof suggestion === "object" && suggestion !== null
    ? { ...(suggestion as SuggestedPortfolioWire), id: suggestionId }
    : null;
}

export async function readClientInstructionSuggestions(
  link: Pick<ClientLink, "kind" | "linkId">,
): Promise<string[]> {
  const rows = await bffJson<unknown>(
    `v1/order-instruction/summary?${clientQuery(link)}&status=${LIVE_STATUSES}`,
  );
  const ids = await Promise.all(
    arrayOf<InstructionSummaryWire>(rows).map((row) => adviceSuggestionOf(row, {})),
  );
  return ids.filter((id): id is string => id !== null);
}

export async function readLinkSubject(
  kind: ClientKind,
  linkId: string,
  lane: ReadLane = {},
): Promise<string | null> {
  const path = kind === "individual" ? "v1/individual-client" : "v1/corporation-client";
  const row = await bffJson<Record<string, unknown>>(`${path}/${encodeURIComponent(linkId)}`, lane);
  const subject = row?.[kind === "individual" ? "individual_id" : "corporation_id"];
  return typeof subject === "string" && subject !== "" ? subject : null;
}

export async function listFirmAdvisors(
  ownerCorporationId: string | null,
  lane: ReadLane = {},
): Promise<string[]> {
  const advisors = arrayOf<AdvisorWire>(await bffJson<unknown>("v1/financial-advisor", lane));
  return advisors.flatMap((advisor) =>
    advisor.id && (ownerCorporationId === null || advisor.corporation_id === ownerCorporationId)
      ? [advisor.id]
      : [],
  );
}

export async function listAdvisorPeople(lane: ReadLane = {}): Promise<Map<string, string>> {
  const advisors = await bffJson<unknown>("v1/financial-advisor", lane);
  const people = new Map<string, string>();
  for (const advisor of arrayOf<AdvisorWire>(advisors)) {
    if (advisor.id && advisor.individual_id) people.set(advisor.id, advisor.individual_id);
  }
  return people;
}

export async function readSuggestedPortfolios(
  link: ClientLink,
  lane: ReadLane = {},
): Promise<SuggestedPortfolioWire[]> {
  const rows = await bffJson<unknown>(
    `v1/quant-portfolio/suggested-portfolio?${clientQuery(link)}`,
    lane,
  );
  return arrayOf<SuggestedPortfolioWire>(rows).filter((row) => typeof row.id === "string");
}

export async function readClientName(
  link: ClientLink,
  lane: ReadLane = {},
): Promise<string | null> {
  if (link.kind === "individual") return readPersonName(link.subjectId, lane);
  const corporation = await bffJson<{ name?: string | null }>(
    `v1/corporation/${encodeURIComponent(link.subjectId)}`,
    lane,
  );
  return nonEmpty(corporation?.name);
}

export async function readPersonName(
  individualId: string,
  lane: ReadLane = {},
): Promise<string | null> {
  const person = await fetchIndividualById(individualId, { background: lane.background });
  return nonEmpty(person?.fullName);
}

export async function readPortfolioName(
  quantPortfolioId: string,
  lane: ReadLane = {},
): Promise<string | null> {
  const read = await readQuantPortfolio(quantPortfolioId, lane);
  if (!read.ok || typeof read.json !== "object" || read.json === null) return null;
  return nonEmpty((read.json as { name?: string | null }).name);
}

export async function readSetQuantifiedAt(
  transactionSetId: string,
  lane: ReadLane = {},
): Promise<string | null> {
  const read = await readTransactionSet(transactionSetId, lane);
  if (!read.ok) return null;
  return setQuantifiedAt(orderSetOf(read.json, transactionSetId));
}

function activeLinks(json: unknown, kind: ClientKind, subjectField: string): ClientLink[] {
  return arrayOf<ClientLinkWire>(json).flatMap((row): ClientLink[] => {
    const subjectId = row[subjectField];
    if (typeof row.id !== "string" || typeof subjectId !== "string") return [];
    const span = {
      start_at_inclusive: row.start_at_inclusive,
      end_at_inclusive: row.end_at_inclusive,
    };
    if (relationshipEnded(span) || !relationshipStarted(span)) return [];
    return [
      {
        key: `${kind}:${row.id}`,
        kind,
        linkId: row.id,
        subjectId,
        advisorId: nonEmpty(row.primary_financial_advisor_id),
      },
    ];
  });
}

function arrayOf<T>(json: unknown): T[] {
  return Array.isArray(json)
    ? json.filter((item): item is T => typeof item === "object" && item !== null)
    : [];
}

function nonEmpty(value: string | null | undefined): string | null {
  return typeof value === "string" && value.trim() !== "" ? value.trim() : null;
}

function clientQuery(link: Pick<ClientLink, "kind" | "linkId">): string {
  const field = link.kind === "individual" ? "individual_client_id" : "corporation_client_id";
  return `${field}=${encodeURIComponent(link.linkId)}`;
}

async function adviceSuggestionOf(
  instruction: Pick<InstructionSummaryWire, "advice_individual_v1_id" | "advice_corporation_v1_id">,
  lane: ReadLane,
): Promise<string | null> {
  const path = instruction.advice_individual_v1_id
    ? `v1/advice-individual/${encodeURIComponent(instruction.advice_individual_v1_id)}`
    : instruction.advice_corporation_v1_id
      ? `v1/advice-corporation/${encodeURIComponent(instruction.advice_corporation_v1_id)}`
      : null;
  if (path === null) return null;
  const advice = await bffJson<{ portfolio_quant_id?: string | number | null }>(path, lane);
  const suggestionId = advice?.portfolio_quant_id;
  return suggestionId === null || suggestionId === undefined ? null : String(suggestionId);
}

interface ClientLinkWire {
  id?: string;
  primary_financial_advisor_id?: string | null;
  start_at_inclusive?: string | null;
  end_at_inclusive?: string | null;
  [field: string]: unknown;
}

interface AdvisorWire {
  id?: string;
  individual_id?: string | null;
  corporation_id?: string | null;
}

interface InstructionSummaryWire {
  id?: string;
  created_at?: string | null;
  financial_advisor_id?: string | null;
  advice_individual_v1_id?: string | null;
  advice_corporation_v1_id?: string | null;
  individual_client_id?: string | null;
  corporation_client_id?: string | null;
  scrive_document?: {
    status?: string | null;
    document_binary_file_id?: string | null;
    document_sealed_binary_file_id?: string | null;
    recipients?: ({ status?: string | null } | null)[] | null;
  } | null;
}

const INSTRUCTION_STATUSES: readonly string[] = ["DRAFT", "PENDING", "COMPLETED", "CANCELED"];
const LISTED_STATUSES = INSTRUCTION_STATUSES.join(",");
const LIVE_STATUSES = "DRAFT,PENDING,COMPLETED";
